import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Realtime transcription via ElevenLabs Scribe realtime WebSocket.
 *
 * Flow:
 *   1. `start()` → POST /api/subject/realtime-token to get a single-use token
 *   2. Open WebSocket to wss://api.elevenlabs.io/v1/speech-to-text/realtime?token=...
 *   3. Capture mic via getUserMedia + AudioContext → ScriptProcessor (or AudioWorklet)
 *   4. Downsample to 16kHz PCM int16, base64-encode each ~250ms chunk
 *   5. Send {message_type: "input_audio_chunk", audio_base_64: "...", sample_rate: 16000}
 *   6. Read incoming partial_transcript / committed_transcript messages → update state
 *   7. `stop()` → send commit:true, await committed_transcript, close socket
 *
 * Exposed state:
 *   - status: idle | connecting | listening | finalizing | error
 *   - interim: best-effort partial transcript while user is still talking
 *   - final: settled transcript after commit
 *   - language: detected language (if include_language_detection was on)
 *   - durationMs: length of audio sent
 */

const TARGET_SAMPLE_RATE = 16000;
const CHUNK_MS = 250;

export type RealtimeStatus =
  | "idle"
  | "connecting"
  | "listening"
  | "finalizing"
  | "error";

export type RealtimeError =
  | "permission_denied"
  | "no_token"
  | "ws_failed"
  | "auth_error"
  | "quota_exceeded"
  | "rate_limited"
  | "no_audio";

export type UseRealtimeTranscriptionOptions = {
  /** Path to the server endpoint that mints a single-use ElevenLabs realtime token. */
  tokenEndpoint?: string;
  /** Extra headers for the token request (e.g. the open survey's x-survey-access). */
  tokenHeaders?: Record<string, string>;
  /** Optional language hint; otherwise auto-detect. */
  languageHint?: string;
  /** Called once when the final transcript settles. */
  onFinal?: (result: { text: string; language: string; durationMs: number }) => void;
  /** Called on any error condition. */
  onError?: (reason: RealtimeError) => void;
};

export type UseRealtimeTranscriptionReturn = {
  status: RealtimeStatus;
  interim: string;
  final: string;
  language: string | null;
  durationMs: number;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  reset: () => void;
};

export function useRealtimeTranscription(
  opts: UseRealtimeTranscriptionOptions = {},
): UseRealtimeTranscriptionReturn {
  const [status, setStatus] = useState<RealtimeStatus>("idle");
  const [interim, setInterim] = useState("");
  const [final, setFinal] = useState("");
  const [language, setLanguage] = useState<string | null>(null);
  const [durationMs, setDurationMs] = useState(0);

  const wsRef = useRef<WebSocket | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const startedAtRef = useRef<number | null>(null);
  const bufferRef = useRef<Float32Array[]>([]);
  const chunkTimerRef = useRef<number | null>(null);

  const cleanup = useCallback(() => {
    if (chunkTimerRef.current !== null) {
      window.clearInterval(chunkTimerRef.current);
      chunkTimerRef.current = null;
    }
    try {
      processorRef.current?.disconnect();
      sourceRef.current?.disconnect();
    } catch {
      /* ignore */
    }
    processorRef.current = null;
    sourceRef.current = null;
    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
      void audioCtxRef.current.close();
    }
    audioCtxRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (wsRef.current && wsRef.current.readyState <= 1) {
      try {
        wsRef.current.close();
      } catch {
        /* ignore */
      }
    }
    wsRef.current = null;
    bufferRef.current = [];
    startedAtRef.current = null;
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const start = useCallback(async () => {
    if (status !== "idle") return;
    setStatus("connecting");
    setInterim("");
    setFinal("");
    setLanguage(null);

    // 1. Get realtime token from our server
    let token: string;
    try {
      const tokRes = await fetch(opts.tokenEndpoint ?? "/api/subject/realtime-token", {
        method: "POST",
        headers: { "content-type": "application/json", ...(opts.tokenHeaders ?? {}) },
        body: JSON.stringify({}),
        credentials: "include",
      });
      if (!tokRes.ok) {
        setStatus("error");
        opts.onError?.("no_token");
        return;
      }
      const tokData = (await tokRes.json()) as { token: string };
      token = tokData.token;
    } catch {
      setStatus("error");
      opts.onError?.("no_token");
      return;
    }

    // 2. Mic
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
    } catch {
      setStatus("error");
      opts.onError?.("permission_denied");
      return;
    }

    // 3. WebSocket
    const params = new URLSearchParams({
      token,
      model_id: "scribe_v2_realtime",
      audio_format: "pcm_16000",
      commit_strategy: "manual",
      include_language_detection: "true",
    });
    if (opts.languageHint && opts.languageHint !== "auto") {
      params.set("language_code", opts.languageHint);
    }
    const ws = new WebSocket(
      `wss://api.elevenlabs.io/v1/speech-to-text/realtime?${params.toString()}`,
    );
    wsRef.current = ws;

    ws.addEventListener("error", () => {
      setStatus("error");
      opts.onError?.("ws_failed");
      cleanup();
    });

    ws.addEventListener("message", (evt) => {
      try {
        const msg = JSON.parse(evt.data) as {
          message_type: string;
          text?: string;
          detected_language?: string;
        };
        if (msg.message_type === "session_started") {
          // Server confirmed — now start audio
        } else if (msg.message_type === "partial_transcript") {
          if (msg.text) setInterim(msg.text);
        } else if (msg.message_type === "committed_transcript" || msg.message_type === "committed_transcript_with_timestamps") {
          if (msg.text) {
            setFinal(msg.text);
            setInterim("");
          }
          if (msg.detected_language) setLanguage(msg.detected_language);
          setStatus("idle");
          const dur = startedAtRef.current ? Date.now() - startedAtRef.current : 0;
          setDurationMs(dur);
          opts.onFinal?.({
            text: msg.text ?? "",
            language: msg.detected_language ?? "en",
            durationMs: dur,
          });
          cleanup();
        } else if (msg.message_type === "auth_error") {
          setStatus("error");
          opts.onError?.("auth_error");
          cleanup();
        } else if (msg.message_type === "quota_exceeded") {
          setStatus("error");
          opts.onError?.("quota_exceeded");
          cleanup();
        } else if (msg.message_type === "rate_limited") {
          setStatus("error");
          opts.onError?.("rate_limited");
          cleanup();
        } else if (msg.message_type === "insufficient_audio_activity") {
          setStatus("error");
          opts.onError?.("no_audio");
          cleanup();
        }
      } catch {
        /* ignore unparseable */
      }
    });

    await new Promise<void>((resolve, reject) => {
      ws.addEventListener("open", () => resolve(), { once: true });
      ws.addEventListener("error", () => reject(), { once: true });
    }).catch(() => {
      setStatus("error");
      opts.onError?.("ws_failed");
      cleanup();
      throw new Error("ws_failed");
    });

    // 4. Audio pipeline: source → processor → buffer → 250ms flush → WS
    const audioCtx = new AudioContext();
    audioCtxRef.current = audioCtx;
    const source = audioCtx.createMediaStreamSource(stream);
    sourceRef.current = source;
    const processor = audioCtx.createScriptProcessor(4096, 1, 1);
    processorRef.current = processor;

    processor.onaudioprocess = (e) => {
      const input = e.inputBuffer.getChannelData(0);
      bufferRef.current.push(new Float32Array(input));
    };
    source.connect(processor);
    processor.connect(audioCtx.destination);

    startedAtRef.current = Date.now();
    setStatus("listening");

    // Flush buffered audio every CHUNK_MS as a downsampled int16 base64 packet
    const sourceRate = audioCtx.sampleRate;
    chunkTimerRef.current = window.setInterval(() => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
      if (bufferRef.current.length === 0) return;
      const merged = mergeFloat32(bufferRef.current);
      bufferRef.current = [];
      const downsampled = downsampleBuffer(merged, sourceRate, TARGET_SAMPLE_RATE);
      const pcm16 = floatTo16BitPCM(downsampled);
      const b64 = uint8ToBase64(new Uint8Array(pcm16.buffer));
      wsRef.current.send(
        JSON.stringify({
          message_type: "input_audio_chunk",
          audio_base_64: b64,
          sample_rate: TARGET_SAMPLE_RATE,
        }),
      );
    }, CHUNK_MS);
  }, [status, opts, cleanup]);

  const stop = useCallback(async () => {
    if (status !== "listening") {
      cleanup();
      return;
    }
    setStatus("finalizing");

    // Flush any remaining audio + commit=true
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      if (bufferRef.current.length > 0 && audioCtxRef.current) {
        const merged = mergeFloat32(bufferRef.current);
        bufferRef.current = [];
        const downsampled = downsampleBuffer(merged, audioCtxRef.current.sampleRate, TARGET_SAMPLE_RATE);
        const pcm16 = floatTo16BitPCM(downsampled);
        const b64 = uint8ToBase64(new Uint8Array(pcm16.buffer));
        wsRef.current.send(
          JSON.stringify({
            message_type: "input_audio_chunk",
            audio_base_64: b64,
            commit: true,
            sample_rate: TARGET_SAMPLE_RATE,
          }),
        );
      } else {
        wsRef.current.send(
          JSON.stringify({
            message_type: "input_audio_chunk",
            audio_base_64: "",
            commit: true,
            sample_rate: TARGET_SAMPLE_RATE,
          }),
        );
      }
    }
    // Wait up to 4s for committed_transcript before giving up
    await new Promise<void>((resolve) => {
      const t = window.setTimeout(resolve, 4000);
      const handler = () => {
        window.clearTimeout(t);
        resolve();
      };
      // status becomes "idle" inside the message handler when commit lands
      const interval = window.setInterval(() => {
        if (statusRef.current === "idle") {
          window.clearInterval(interval);
          handler();
        }
      }, 50);
    });
  }, [status, cleanup]);

  // Keep a ref of status so stop() can poll without re-creating
  const statusRef = useRef(status);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const reset = useCallback(() => {
    cleanup();
    setStatus("idle");
    setInterim("");
    setFinal("");
    setLanguage(null);
    setDurationMs(0);
  }, [cleanup]);

  return { status, interim, final, language, durationMs, start, stop, reset };
}

// --- Audio helpers ---

function mergeFloat32(buffers: Float32Array[]): Float32Array {
  let totalLen = 0;
  for (const b of buffers) totalLen += b.length;
  const out = new Float32Array(totalLen);
  let offset = 0;
  for (const b of buffers) {
    out.set(b, offset);
    offset += b.length;
  }
  return out;
}

function downsampleBuffer(buffer: Float32Array, sourceRate: number, targetRate: number): Float32Array {
  if (sourceRate === targetRate) return buffer;
  if (targetRate > sourceRate) throw new Error("upsample_not_supported");
  const ratio = sourceRate / targetRate;
  const newLen = Math.round(buffer.length / ratio);
  const result = new Float32Array(newLen);
  let offsetResult = 0;
  let offsetBuffer = 0;
  while (offsetResult < newLen) {
    const nextOffset = Math.round((offsetResult + 1) * ratio);
    let accum = 0;
    let count = 0;
    for (let i = offsetBuffer; i < nextOffset && i < buffer.length; i++) {
      accum += buffer[i];
      count += 1;
    }
    result[offsetResult] = count > 0 ? accum / count : 0;
    offsetResult += 1;
    offsetBuffer = nextOffset;
  }
  return result;
}

function floatTo16BitPCM(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

function uint8ToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunkSize)));
  }
  return btoa(binary);
}
